import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import StickySubNav from '../../components/ui/StickySubNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExecutiveSummaryHero from './components/ExecutiveSummaryHero';
import RoleBasedInsights from './components/RoleBasedInsights';
import TransparentEconomics from './components/TransparentEconomics';
import CompetitiveDifferentiation from './components/CompetitiveDifferentiation';
import StreamlinedMethodology from './components/StreamlinedMethodology';
import ServiceCategories from '../gcc-setup-services-comprehensive-mastery/components/ServiceCategories';
import ClientTestimonials from '../../components/ClientTestimonials';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';

// Define page sections for sticky subnav - matches dropdown menu
const pageSections = [
  { id: 'launch-blueprint', name: 'Launch Blueprint', icon: 'Flag' },
  { id: 'ai-intelligence', name: 'AI Intelligence', icon: 'Sparkles' },
  { id: 'roi-calculator', name: 'ROI Calculator', icon: 'Calculator' },
  { id: 'pricing', name: 'Pricing', icon: 'DollarSign' },
  { id: 'methodology', name: 'Methodology', icon: 'Layers' },
  { id: 'differentiation', name: 'Why Bootminds', icon: 'Award' }
];

const GccSetupExecutiveEdition = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Handle anchor scrolling
  useAnchorScroll();
  
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'GCC Setup - Executive Edition', url: '/gcc-setup-executive-edition' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "GCC Setup Services - Executive Edition",
    "description": "Fortune 500-grade Global Capability Center setup in 90 days with zero-risk guarantees, transparent pricing, and executive-first approach. Proven track record with $20M+ in client savings delivered during the 2018-2024 Intelliswift alliance.",
    "provider": {
      "@type": "Organization",
      "name": "Bootminds"
    },
    "serviceType": "Strategic Business Consulting",
    "areaServed": "Global",
    "offers": {
      "@type": "Offer",
      "description": "Complete GCC setup with 90-day launch guarantee, compliance assurance with milestone-based guarantees, and transparent all-in pricing"
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    // Scroll to role-based insights section after selection
    setTimeout(() => {
      const element = document.getElementById('role-insights');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <>
      <SEOHead
        title="GCC Setup - Executive Edition | 90-Day Launch Guarantee | Bootminds"
        description="Fortune 500-grade GCC in 90 days, not 18 months. Zero-risk approach with transparent pricing, compliance guarantee, and proven outcomes. 1,000+ professionals placed across our team's GCC engagements since 2018."
        keywords="GCC setup executive, 90-day GCC launch, Global Capability Center consulting, CEO CFO CIO GCC services, transparent GCC pricing, risk-free GCC setup, India GCC establishment"
        canonicalUrl="/services"
        breadcrumbs={breadcrumbs}
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        <main className="content-animate">
          {/* 1. Executive Summary Hero - Lead with impact */}
          <ExecutiveSummaryHero onRoleSelect={handleRoleSelect} />

          {/* Sticky Subnav - matches dropdown menu */}
          <StickySubNav sections={pageSections} topOffset="top-16 lg:top-20" />

          {/* 2. Role-Based Insights - Personalized content if role selected */}
          {selectedRole && (
            <div id="role-insights" className="scroll-mt-40">
              <RoleBasedInsights selectedRole={selectedRole} />
            </div>
          )}

          {/* 3. Launch Blueprint - 90-day methodology */}
          <section id="launch-blueprint" className="scroll-mt-40">
            <ServiceCategories showOnlyBlueprint={true} />
          </section>

          {/* 4. AI Intelligence - AI-powered solutions */}
          <section id="ai-intelligence" className="scroll-mt-40">
            <ServiceCategories showOnlyAI={true} />
          </section>

          {/* 5. ROI Calculator — link to the full interactive suite (/calculator) */}
          <section id="roi-calculator" className="scroll-mt-40 py-16 lg:py-20 bg-background-alt">
            <div className="container-executive">
              <div className="max-w-3xl mx-auto text-center space-y-6 bg-card border border-border rounded-2xl p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
                  <Icon name="Calculator" size={24} color="var(--color-primary)" />
                </div>
                <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
                  Model Your GCC ROI
                </h2>
                <p className="text-text-secondary">
                  Compare locations, estimate savings, and project your payback with our full suite of
                  interactive ROI and planning calculators.
                </p>
                <Button variant="default" size="default" iconName="ArrowRight" iconPosition="right" asChild>
                  <Link to="/calculator">Open the ROI Calculators</Link>
                </Button>
              </div>
            </div>
          </section>

          {/* 6. Transparent Economics - Pricing & cost breakdown */}
          <section id="pricing" className="scroll-mt-40">
            <TransparentEconomics />
          </section>

          {/* 7. Streamlined Methodology - 90-day process */}
          <section id="methodology" className="scroll-mt-40">
            <StreamlinedMethodology />
          </section>

          {/* 8. Competitive Differentiation - Why Bootminds */}
          <section id="differentiation" className="scroll-mt-40">
            <CompetitiveDifferentiation />
          </section>

          {/* 9. Engagement Models — link to the assessment / full finder */}
          <section className="py-16 lg:py-20">
            <div className="container-executive">
              <div className="max-w-3xl mx-auto text-center space-y-6 bg-card border border-border rounded-2xl p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
                  <Icon name="Layers" size={24} color="var(--color-primary)" />
                </div>
                <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
                  Find Your Ideal Engagement Model
                </h2>
                <p className="text-text-secondary">
                  Company-owned, joint venture, BOT, or virtual GCC — take the 5-question assessment to
                  see which partnership structure fits your goals.
                </p>
                <Button variant="default" size="default" iconName="ArrowRight" iconPosition="right" asChild>
                  <Link to="/assessment">Take the Assessment</Link>
                </Button>
              </div>
            </div>
          </section>

          {/* 10. Social Proof - Client testimonials (honest empty-state → case studies) */}
          <ClientTestimonials />

          {/* 11. Final Executive CTA */}
          <ExecutiveFinalCTA selectedRole={selectedRole} />
        </main>
        <SharedFooter />
      </div>
    </>
  );
};

// Final CTA Section
const ExecutiveFinalCTA = ({ selectedRole }) => {
  const roleSpecificCTA = {
    CEO: {
      headline: 'Ready to Accelerate Your Global Strategy?',
      subheadline: 'Join 50+ Fortune 500 CEOs who transformed their operations with Bootminds',
      primaryAction: 'Schedule CEO Briefing',
      secondaryAction: 'Download Board Deck'
    },
    CFO: {
      headline: 'Ready for Predictable, Transparent Economics?',
      subheadline: 'Get your personalized ROI model and total cost breakdown in 24 hours',
      primaryAction: 'Calculate Your ROI',
      secondaryAction: 'Review Financial Model'
    },
    CIO: {
      headline: 'Ready for Enterprise-Grade Technology Excellence?',
      subheadline: 'Review our security architecture and integration playbook',
      primaryAction: 'Security Assessment',
      secondaryAction: 'Review Tech Stack'
    }
  };

  const defaultCTA = {
    headline: 'Ready to Launch Your GCC in 90 Days?',
    subheadline: 'Join leading enterprises who chose the zero-risk approach — $20M+ in client savings delivered during the 2018-2024 Intelliswift alliance',
    primaryAction: 'Start Your LaunchPad Sprint',
    secondaryAction: 'Schedule Consultation'
  };

  const cta = roleSpecificCTA[selectedRole] || defaultCTA;

  return (
    <section className="py-20 bg-primary text-primary-foreground">
      <div className="container-executive">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-headline font-bold mb-6">
            {cta.headline}
          </h2>
          <p className="text-xl text-primary-foreground/90 mb-12 max-w-3xl mx-auto">
            {cta.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button className="px-8 py-4 bg-background text-text-primary rounded-lg font-semibold text-lg hover:bg-background transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105">
              {cta.primaryAction}
            </button>
            <button className="px-8 py-4 bg-transparent border-2 border-primary-foreground text-primary-foreground rounded-lg font-semibold text-lg hover:bg-primary-foreground/10 transition-all duration-300">
              {cta.secondaryAction}
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-primary-foreground/80 text-sm">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span>GCC Programs Led Since 2018</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>Compliance Assurance with Milestone Guarantees</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>90-Day Launch Guarantee</span>
            </div>
          </div>

          <div className="mt-12 pt-12 border-t border-primary-foreground/20">
            <p className="text-primary-foreground/70 text-sm mb-4">
              Trusted by Fortune 500 Companies Worldwide
            </p>
            <div className="text-primary-foreground/60 text-xs"> ISO 27001 & SOC2 Certified | Zero Security Incidents
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GccSetupExecutiveEdition;
